import React, { ReactElement, useState } from "react";
import { Controller } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { isNil } from "ramda";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { IContainersList, IMailContainersList, IZoneList } from "types/api/appsApiInterface";
import { IMailNotPagedData } from "types/api/mailApiInterface";
import { IAllWebsiteData } from "types/api/websitesApiInterface";
import { appPermissionsType } from "types/global/user";

type sectionType =
 | "editPowerOffOnSite"
 | "editPowerOffOnMail"
 | "editPowerOffOnZone"
 | "showZoneRecords"
 | "editZoneRecords"
 | "showMailDetails"
 | "editMailDetails"
 | "showMailbox"
 | "editMailbox"
 | "showAutomaticMail"
 | "editAutomaticMail"
 | "showWhiteBlackMailList"
 | "editWhiteBlackMailList"
 | "showSiteDetails"
 | "editSiteDetails"
 | "showSiteFtpCredentials"
 | "editSiteFtpCredentials"
 | "showSiteAlias"
 | "editSiteAlias"
 | "showSiteDatabase"
 | "editSiteDatabase"
 | "showSiteBackups"
 | "editSiteBackups"
 | "showSiteCronjobs"
 | "editSiteCronjobs"
 | "editSsl"
 | "editContainerSiteDetails"
 | "showContainerSiteDetails"
 | "editContainerDetails"
 | "editContainerFilemanager"
 | "showContainerDbDetails"
 | "editContainerDbDetails"
 | "showContainerFtpDetails"
 | "editContainerFtpDetails"
 | "showContainerSiteCronjob"
 | "editContainerSiteCronjob"
 | "showContainerSiteBackups"
 | "editContainerSiteBackups"
 | "showContainerSitePlugins"
 | "editContainerSitePlugins"
 | "showContainerMailDetails"
 | "editContainerMailDetails"
 | "editContainerMailLimits"
 | "showContainerMailMailboxes"
 | "editContainerMailMailboxes"
 | "showContainerMailAlias"
 | "editContainerMailAlias"
 | "showContainerMailboxesAlias"
 | "editContainerMailboxesAlias"
 | "showContainerMailFailtoban"
 | "editContainerMailFailtoban"
 | "showContainerMailWhitelist"
 | "editContainerMailWhitelist"
 | "showContainerMailBlacklist"
 | "editContainerMailBlacklist";

type arrayType = "sites" | "mails" | "zones" | "containersite" | "containermail";

const AppsSection = ({
 control,
 sites,
 mails,
 zone,
 containers,
 containersmail,
 selectedSites,
 selectedMails,
 selectedZone,
 selectedContainers,
 selectedMailContainers,
 appsPermissions,
 setApp,
 addApp,
 setPermission
}: {
 control: any;
 sites: Array<IAllWebsiteData>;
 mails: Array<IMailNotPagedData>;
 zone: Array<IZoneList>;
 containers: Array<IContainersList>;
 containersmail: Array<IMailContainersList>;
 selectedSites: Array<number>;
 selectedMails: Array<number>;
 selectedZone: Array<number>;
 selectedContainers: Array<number>;
 selectedMailContainers: Array<number>;
 appsPermissions: appPermissionsType;
 setApp: (section: arrayType, app: Array<number>) => void;
 addApp: (section: arrayType, value: Array<{ id: number; label: string }>) => void;
 setPermission: (section: sectionType, value: boolean) => void;
}): ReactElement => {
 const intl = useIntl();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("md"));

 const [section, setSection] = useState<number>(0);
 const [allSites, setAllSites] = useState<boolean>(false);
 const [allMails, setAllMails] = useState<boolean>(false);
 const [allZones, setAllZones] = useState<boolean>(false);
 const [allContainers, setAllContainers] = useState<boolean>(false);
 const [allMailContainers, setAllMailContainers] = useState<boolean>(false);

 const addAllApps = (
  section: arrayType,
  sectionArray: Array<number>,
  sectionData: Array<IAllWebsiteData | IMailNotPagedData | IZoneList | IContainersList>
 ): void => {
  let tempData: Array<number> = sectionArray;
  sectionData
   .filter((element) => !sectionArray.includes(element.id))
   .map((element) => tempData.push(element.id));
  setApp(section, tempData);
 };

 const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
  setSection(newValue);
 };

 const RenderTabContent = (): ReactElement => {
  switch (section) {
   case 0:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allSites}
         onChange={(e, checked) => {
          setAllSites(checked);
          if (checked) addAllApps("sites", selectedSites, sites);
          else setApp("sites", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllSites" })}
      />
      {!allSites && (
       <Controller
        name="sites"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          id="tags-outlined"
          options={sites.map((element) => {
           return {
            id: element.id,
            label: element.site
           };
          })}
          disabled={allSites}
          filterSelectedOptions
          value={sites
           .filter((element) => selectedSites.includes(element.id))
           .map((element) => {
            return {
             id: element.id,
             label: element.site
            };
           })}
          getOptionLabel={(option) => option.label}
          onChange={(e, value) => {
           isNil(value) ? setApp("sites", selectedSites) : addApp("sites", value);
          }}
          renderInput={(params) => (
           <TextField
            {...params}
            label={intl.formatMessage({ id: "teams.usersettings.selectSites" })}
            placeholder="Site..."
           />
          )}
         />
        )}
       />
      )}
      <Stack direction={"row"}>
       <Stack>
        <Controller
         name="editPowerOffOnSite"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.editPowerOffOnSite}
             onChange={(e, checked) => {
              setPermission("editPowerOffOnSite", checked);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.editPowerOffOnSite" })}
          />
         )}
        />
        <Controller
         name="showSiteDetails"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showSiteDetails}
             onChange={(e, checked) => {
              setPermission("showSiteDetails", checked);
              if (!checked) setPermission("editSiteDetails", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showSiteDetails" })}
          />
         )}
        />
        <Controller
         name="showSiteFtpCredentials"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showSiteFtpCredentials}
             onChange={(e, checked) => {
              setPermission("showSiteFtpCredentials", checked);
              if (!checked) setPermission("editSiteFtpCredentials", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showSiteFtpCredentials" })}
          />
         )}
        />
        <Controller
         name="showSiteAlias"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showSiteAlias}
             onChange={(e, checked) => {
              setPermission("showSiteAlias", checked);
              if (!checked) setPermission("editSiteAlias", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showSiteAlias" })}
          />
         )}
        />
        <Controller
         name="showSiteDatabase"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showSiteDatabase}
             onChange={(e, checked) => {
              setPermission("showSiteDatabase", checked);
              if (!checked) setPermission("editSiteDatabase", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showSiteDatabase" })}
          />
         )}
        />
        <Controller
         name="showSiteBackups"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showSiteBackups}
             onChange={(e, checked) => {
              setPermission("showSiteBackups", checked);
              if (!checked) setPermission("editSiteBackups", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showSiteBackups" })}
          />
         )}
        />
        <Controller
         name="showSiteCronjobs"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showSiteCronjobs}
             onChange={(e, checked) => {
              setPermission("showSiteCronjobs", checked);
              if (!checked) setPermission("editSiteCronjobs", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showSiteCronjobs" })}
          />
         )}
        />
       </Stack>
       <Stack>
        {appsPermissions.showSiteDetails && (
         <Controller
          name="editSiteDetails"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editSiteDetails}
              onChange={(e, checked) => {
               setPermission("editSiteDetails", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editSiteDetails" })}
           />
          )}
         />
        )}
        {appsPermissions.showSiteFtpCredentials && (
         <Controller
          name="editSiteFtpCredentials"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editSiteFtpCredentials}
              onChange={(e, checked) => {
               setPermission("editSiteFtpCredentials", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editSiteFtpCredentials" })}
           />
          )}
         />
        )}
        {appsPermissions.showSiteAlias && (
         <Controller
          name="editSiteAlias"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editSiteAlias}
              onChange={(e, checked) => {
               setPermission("editSiteAlias", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editSiteAlias" })}
           />
          )}
         />
        )}
        {appsPermissions.showSiteDatabase && (
         <Controller
          name="editSiteDatabase"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editSiteDatabase}
              onChange={(e, checked) => {
               setPermission("editSiteDatabase", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editSiteDatabase" })}
           />
          )}
         />
        )}
        {appsPermissions.showSiteBackups && (
         <Controller
          name="editSiteBackups"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editSiteBackups}
              onChange={(e, checked) => {
               setPermission("editSiteBackups", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editSiteBackups" })}
           />
          )}
         />
        )}
        {appsPermissions.showSiteCronjobs && (
         <Controller
          name="editSiteCronjobs"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editSiteCronjobs}
              onChange={(e, checked) => {
               setPermission("editSiteCronjobs", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editSiteCronjobs" })}
           />
          )}
         />
        )}
       </Stack>
      </Stack>
     </Stack>
    );
   case 1:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allMails}
         onChange={(e, checked) => {
          setAllMails(checked);
          if (checked) addAllApps("mails", selectedMails, mails);
          else setApp("mails", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllMails" })}
      />
      {!allMails && (
       <Controller
        name="mails"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          id="tags-outlined"
          options={mails.map((element) => {
           return {
            id: element.id,
            label: element.domain
           };
          })}
          disabled={allMails}
          filterSelectedOptions
          value={mails
           .filter((element) => selectedMails.includes(element.id))
           .map((element) => {
            return {
             id: element.id,
             label: element.domain
            };
           })}
          getOptionLabel={(option) => option.label}
          onChange={(e, value) => {
           isNil(value) ? setApp("mails", selectedMails) : addApp("mails", value);
          }}
          renderInput={(params) => (
           <TextField
            {...params}
            label={intl.formatMessage({ id: "teams.usersettings.selectMails" })}
            placeholder="Mail domain..."
           />
          )}
         />
        )}
       />
      )}
      <Stack direction="row">
       <Stack>
        <Controller
         name="editPowerOffOnMail"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.editPowerOffOnMail}
             onChange={(e, checked) => {
              setPermission("editPowerOffOnMail", checked);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.editPowerOffOnMail" })}
          />
         )}
        />
        <Controller
         name="showMailDetails"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showMailDetails}
             onChange={(e, checked) => {
              setPermission("showMailDetails", checked);
              if (!checked) setPermission("editMailDetails", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showMailDetails" })}
          />
         )}
        />
        <Controller
         name="showMailbox"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showMailbox}
             onChange={(e, checked) => {
              setPermission("showMailbox", checked);
              if (!checked) setPermission("editMailbox", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showMailbox" })}
          />
         )}
        />
        <Controller
         name="showAutomaticMail"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showAutomaticMail}
             onChange={(e, checked) => {
              setPermission("showAutomaticMail", checked);
              if (!checked) setPermission("editAutomaticMail", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showAutomaticMail" })}
          />
         )}
        />
        <Controller
         name="showWhiteBlackMailList"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showWhiteBlackMailList}
             onChange={(e, checked) => {
              setPermission("showWhiteBlackMailList", checked);
              if (!checked) setPermission("editWhiteBlackMailList", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showWhiteBlackMailList" })}
          />
         )}
        />
       </Stack>
       <Stack>
        {appsPermissions.showMailDetails && (
         <Controller
          name="editMailDetails"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editMailDetails}
              onChange={(e, checked) => {
               setPermission("editMailDetails", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editMailDetails" })}
           />
          )}
         />
        )}
        {appsPermissions.showMailbox && (
         <Controller
          name="editMailbox"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editMailbox}
              onChange={(e, checked) => {
               setPermission("editMailbox", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editMailbox" })}
           />
          )}
         />
        )}
        {appsPermissions.showAutomaticMail && (
         <Controller
          name="editAutomaticMail"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editAutomaticMail}
              onChange={(e, checked) => {
               setPermission("editAutomaticMail", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editAutomaticMail" })}
           />
          )}
         />
        )}
        {appsPermissions.showWhiteBlackMailList && (
         <Controller
          name="editWhiteBlackMailList"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editWhiteBlackMailList}
              onChange={(e, checked) => {
               setPermission("editWhiteBlackMailList", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editWhiteBlackMailList" })}
           />
          )}
         />
        )}
       </Stack>
      </Stack>
     </Stack>
    );
   case 2:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allZones}
         onChange={(e, checked) => {
          setAllZones(checked);
          if (checked) addAllApps("zones", selectedZone, zone);
          else setApp("zones", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllZones" })}
      />
      {!allZones && (
       <Controller
        name="zones"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          id="tags-outlined"
          options={zone.map((element) => {
           return {
            id: element.id,
            label: element.zone
           };
          })}
          disabled={allZones}
          filterSelectedOptions
          value={zone
           .filter((element) => selectedZone.includes(element.id))
           .map((element) => {
            return {
             id: element.id,
             label: element.zone
            };
           })}
          getOptionLabel={(option) => option.label}
          onChange={(e, value) => {
           isNil(value) ? setApp("zones", selectedZone) : addApp("zones", value);
          }}
          renderInput={(params) => (
           <TextField
            {...params}
            label={intl.formatMessage({ id: "teams.usersettings.selectZones" })}
            placeholder="Zone..."
           />
          )}
         />
        )}
       />
      )}
      <Stack direction="row">
       <Stack>
        <Controller
         name="editPowerOffOnZone"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.editPowerOffOnZone}
             onChange={(e, checked) => {
              setPermission("editPowerOffOnZone", checked);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.editPowerOffOnZone" })}
          />
         )}
        />
        <Controller
         name="showZoneRecords"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showZoneRecords}
             onChange={(e, checked) => {
              setPermission("showZoneRecords", checked);
              if (!checked) setPermission("editZoneRecords", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showZoneRecords" })}
          />
         )}
        />
       </Stack>
       <Stack>
        {appsPermissions.showZoneRecords && (
         <Controller
          name="editZoneRecords"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editZoneRecords}
              onChange={(e, checked) => {
               setPermission("editZoneRecords", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editZoneRecords" })}
           />
          )}
         />
        )}
       </Stack>
      </Stack>
     </Stack>
    );
   case 3:
    return (
     <Stack spacing={2}>
      <Typography variant="subtitle2" textAlign="left">
       <FormattedMessage
        id="teams.usersettings.selectAContainerServer"
        values={{
         remember: (
          <Typography variant="subtitle2" textAlign="left" fontWeight="bold">
           <FormattedMessage id="app.remember" />
          </Typography>
         )
        }}
       />
      </Typography>
      <FormControlLabel
       control={
        <Checkbox
         checked={allContainers}
         onChange={(e, checked) => {
          setAllContainers(checked);
          if (checked) addAllApps("containersite", selectedContainers, containers);
          else setApp("containersite", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllContainerSites" })}
      />
      {!allContainers && (
       <Controller
        name="containersite"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          id="tags-outlined"
          options={containers.map((element) => {
           return {
            id: element.id,
            label: element.site_name
           };
          })}
          disabled={allContainers}
          filterSelectedOptions
          value={containers
           .filter((element) => selectedContainers.includes(element.id))
           .map((element) => {
            return {
             id: element.id,
             label: element.site_name
            };
           })}
          getOptionLabel={(option) => option.label}
          onChange={(e, value) => {
           isNil(value)
            ? setApp("containersite", selectedContainers)
            : addApp("containersite", value);
          }}
          renderInput={(params) => (
           <TextField
            {...params}
            label={intl.formatMessage({ id: "teams.usersettings.selectSites" })}
            placeholder="Site..."
           />
          )}
         />
        )}
       />
      )}
      <Stack direction={"row"}>
       <Stack>
        <Controller
         name="editSsl"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.editSsl}
             onChange={(e, checked) => {
              setPermission("editSsl", checked);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.editSsl" })}
          />
         )}
        />
        <Controller
         name="showContainerSiteDetails"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerSiteDetails}
             onChange={(e, checked) => {
              setPermission("showContainerSiteDetails", checked);
              if (!checked) setPermission("showContainerSiteDetails", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerSiteDetails" })}
          />
         )}
        />
        <Controller
         name="editContainerDetails"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.editContainerDetails}
             onChange={(e, checked) => {
              setPermission("editContainerDetails", checked);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.editContainerDetails" })}
          />
         )}
        />
        <Controller
         name="editContainerFilemanager"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.editContainerFilemanager}
             onChange={(e, checked) => {
              setPermission("editContainerFilemanager", checked);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.editContainerFilemanager" })}
          />
         )}
        />
        <Controller
         name="showContainerDbDetails"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerDbDetails}
             onChange={(e, checked) => {
              setPermission("showContainerDbDetails", checked);
              if (!checked) setPermission("editContainerDbDetails", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerDbDetails" })}
          />
         )}
        />
        <Controller
         name="showContainerFtpDetails"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerFtpDetails}
             onChange={(e, checked) => {
              setPermission("showContainerFtpDetails", checked);
              if (!checked) setPermission("editContainerFtpDetails", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerFtpDetails" })}
          />
         )}
        />
        <Controller
         name="showContainerSiteCronjob"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerSiteCronjob}
             onChange={(e, checked) => {
              setPermission("showContainerSiteCronjob", checked);
              if (!checked) setPermission("editContainerSiteCronjob", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerSiteCronjob" })}
          />
         )}
        />
        <Controller
         name="showContainerSiteBackups"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerSiteBackups}
             onChange={(e, checked) => {
              setPermission("showContainerSiteBackups", checked);
              if (!checked) setPermission("editContainerSiteBackups", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerSiteBackups" })}
          />
         )}
        />
        <Controller
         name="showContainerSitePlugins"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerSitePlugins}
             onChange={(e, checked) => {
              setPermission("showContainerSitePlugins", checked);
              if (!checked) setPermission("editContainerSitePlugins", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerSitePlugins" })}
          />
         )}
        />
       </Stack>
       <Stack>
        {appsPermissions.showContainerSiteDetails && (
         <Controller
          name="editContainerSiteDetails"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerSiteDetails}
              onChange={(e, checked) => {
               setPermission("editContainerSiteDetails", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerSiteDetails" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerDbDetails && (
         <Controller
          name="editContainerDbDetails"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerDbDetails}
              onChange={(e, checked) => {
               setPermission("editContainerDbDetails", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerDbDetails" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerFtpDetails && (
         <Controller
          name="editContainerFtpDetails"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerFtpDetails}
              onChange={(e, checked) => {
               setPermission("editContainerFtpDetails", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerFtpDetails" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerSiteCronjob && (
         <Controller
          name="editContainerSiteCronjob"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerSiteCronjob}
              onChange={(e, checked) => {
               setPermission("editContainerSiteCronjob", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerSiteCronjob" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerSiteBackups && (
         <Controller
          name="editContainerSiteBackups"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerSiteBackups}
              onChange={(e, checked) => {
               setPermission("editContainerSiteBackups", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerSiteBackups" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerSitePlugins && (
         <Controller
          name="editContainerSitePlugins"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerSitePlugins}
              onChange={(e, checked) => {
               setPermission("editContainerSitePlugins", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerSitePlugins" })}
           />
          )}
         />
        )}
       </Stack>
      </Stack>
     </Stack>
    );
   case 4:
    return (
     <Stack spacing={2}>
      <Typography variant="subtitle2" textAlign="left">
       <FormattedMessage
        id="teams.usersettings.selectAContainerMailServer"
        values={{
         remember: (
          <Typography variant="subtitle2" textAlign="left" fontWeight="bold">
           <FormattedMessage id="app.remember" />
          </Typography>
         )
        }}
       />
      </Typography>
      <FormControlLabel
       control={
        <Checkbox
         checked={allMailContainers}
         onChange={(e, checked) => {
          setAllMailContainers(checked);
          if (checked) addAllApps("containermail", selectedContainers, containers);
          else setApp("containermail", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllContainerMail" })}
      />
      {!allMailContainers && (
       <Controller
        name="containermail"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          id="tags-outlined"
          options={containersmail.map((element) => {
           return {
            id: element.id,
            label: element.domain_name
           };
          })}
          disabled={allContainers}
          filterSelectedOptions
          value={containersmail
           .filter((element) => selectedMailContainers.includes(element.id))
           .map((element) => {
            return {
             id: element.id,
             label: element.domain_name
            };
           })}
          getOptionLabel={(option) => option.label}
          onChange={(e, value) => {
           isNil(value)
            ? setApp("containermail", selectedMailContainers)
            : addApp("containermail", value);
          }}
          renderInput={(params) => (
           <TextField
            {...params}
            label={intl.formatMessage({ id: "teams.usersettings.selectMails" })}
            placeholder="Maildomain..."
           />
          )}
         />
        )}
       />
      )}
      <Stack direction={"row"}>
       <Stack>
        <Controller
         name="showContainerMailDetails"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerMailDetails}
             onChange={(e, checked) => {
              setPermission("showContainerMailDetails", checked);
              if (!checked) {
               setPermission("editContainerMailDetails", false);
               setPermission("editContainerMailLimits", false);
              }
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerMailDetails" })}
          />
         )}
        />
        <Controller
         name="showContainerMailMailboxes"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerMailMailboxes}
             onChange={(e, checked) => {
              setPermission("showContainerMailMailboxes", checked);
              if (!checked) setPermission("editContainerMailMailboxes", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerMailMailboxes" })}
          />
         )}
        />
        <Controller
         name="showContainerMailAlias"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerMailAlias}
             onChange={(e, checked) => {
              setPermission("showContainerMailAlias", checked);
              if (!checked) setPermission("editContainerMailAlias", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerMailAlias" })}
          />
         )}
        />
        <Controller
         name="showContainerMailboxesAlias"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerMailboxesAlias}
             onChange={(e, checked) => {
              setPermission("showContainerMailboxesAlias", checked);
              if (!checked) setPermission("editContainerMailboxesAlias", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerMailboxesAlias" })}
          />
         )}
        />
        <Controller
         name="showContainerMailFailtoban"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerMailFailtoban}
             onChange={(e, checked) => {
              setPermission("showContainerMailFailtoban", checked);
              if (!checked) setPermission("editContainerMailFailtoban", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerMailFailtoban" })}
          />
         )}
        />
        <Controller
         name="showContainerMailWhitelist"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerMailWhitelist}
             onChange={(e, checked) => {
              setPermission("showContainerMailWhitelist", checked);
              if (!checked) setPermission("editContainerMailWhitelist", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerMailWhitelist" })}
          />
         )}
        />
        <Controller
         name="showContainerMailBlacklist"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.showContainerMailBlacklist}
             onChange={(e, checked) => {
              setPermission("showContainerMailBlacklist", checked);
              if (!checked) setPermission("editContainerMailBlacklist", false);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.showContainerMailBlacklist" })}
          />
         )}
        />
       </Stack>
       <Stack>
        {appsPermissions.showContainerMailDetails && (
         <Controller
          name="editContainerMailDetails"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerMailDetails}
              onChange={(e, checked) => {
               setPermission("editContainerMailDetails", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerMailDetails" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerMailMailboxes && (
         <Controller
          name="editContainerMailMailboxes"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerMailMailboxes}
              onChange={(e, checked) => {
               setPermission("editContainerMailMailboxes", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerMailMailboxes" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerMailAlias && (
         <Controller
          name="editContainerMailAlias"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerMailAlias}
              onChange={(e, checked) => {
               setPermission("editContainerMailAlias", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerMailAlias" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerMailboxesAlias && (
         <Controller
          name="editContainerMailboxesAlias"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerMailboxesAlias}
              onChange={(e, checked) => {
               setPermission("editContainerMailboxesAlias", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerMailboxesAlias" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerMailFailtoban && (
         <Controller
          name="editContainerMailFailtoban"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerMailFailtoban}
              onChange={(e, checked) => {
               setPermission("editContainerMailFailtoban", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerMailFailtoban" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerMailWhitelist && (
         <Controller
          name="editContainerMailWhitelist"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerMailWhitelist}
              onChange={(e, checked) => {
               setPermission("editContainerMailWhitelist", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerMailWhitelist" })}
           />
          )}
         />
        )}
        {appsPermissions.showContainerMailBlacklist && (
         <Controller
          name="editContainerMailBlacklist"
          control={control}
          render={({ field }) => (
           <FormControlLabel
            control={
             <Checkbox
              {...field}
              checked={appsPermissions.editContainerMailBlacklist}
              onChange={(e, checked) => {
               setPermission("editContainerMailBlacklist", checked);
              }}
             />
            }
            label={intl.formatMessage({ id: "teams.usersettings.editContainerMailBlacklist" })}
           />
          )}
         />
        )}
       </Stack>
      </Stack>
     </Stack>
    );
   default:
    return (
     <Stack spacing={2}>
      <Controller
       name="sites"
       control={control}
       render={({ field }) => (
        <Autocomplete
         {...field}
         multiple
         id="tags-outlined"
         options={sites.map((element) => {
          return {
           id: element.id,
           label: element.site
          };
         })}
         filterSelectedOptions
         value={sites
          .filter((element) => selectedSites.includes(element.id))
          .map((element) => {
           return {
            id: element.id,
            label: element.site
           };
          })}
         getOptionLabel={(option) => option.label}
         onChange={(e, value) => {
          isNil(value) ? setApp("sites", selectedSites) : addApp("sites", value);
         }}
         renderInput={(params) => (
          <TextField
           {...params}
           label={intl.formatMessage({ id: "teams.usersettings.selectSites" })}
           placeholder="Site..."
          />
         )}
        />
       )}
      />
      <Stack>
       <Stack>
        <Controller
         name="editPowerOffOnSite"
         control={control}
         render={({ field }) => (
          <FormControlLabel
           control={
            <Checkbox
             {...field}
             checked={appsPermissions.editPowerOffOnSite}
             onChange={(e, checked) => {
              setPermission("editPowerOffOnSite", checked);
             }}
            />
           }
           label={intl.formatMessage({ id: "teams.usersettings.editPowerOffOnSite" })}
          />
         )}
        />
       </Stack>
      </Stack>
     </Stack>
    );
  }
 };

 return (
  <Accordion>
   <AccordionSummary expandIcon={<ExpandMoreIcon />}>Apps</AccordionSummary>
   <AccordionDetails>
    <Tabs
     value={section}
     onChange={handleChangeTab}
     variant={desktopViewPort ? "standard" : "scrollable"}
     scrollButtons={true}
     allowScrollButtonsMobile
     sx={{ mb: 2 }}>
     <Tab label={intl.formatMessage({ id: "app.sitesTitle" })} />
     <Tab label={intl.formatMessage({ id: "app.mail" })} />
     <Tab label={intl.formatMessage({ id: "app.zone" })} />
     <Tab label={intl.formatMessage({ id: "app.containersite" })} />
     <Tab label={intl.formatMessage({ id: "app.containermail" })} />
    </Tabs>
    <RenderTabContent />
   </AccordionDetails>
  </Accordion>
 );
};

export default AppsSection;
